import { Subject } from "rxjs";
import { jsonTryParse } from "core/utils/json";

class LocalStoreManager {
  static syncListenerInitialised = false;
  static DBKEY_USER_DATA = "user_data";
  static DBKEY_SYNC_KEYS = "sync_keys";
  #syncKeys = [];
  #initEvent = new Subject();

  #reservedKeys = [
    "sync_keys",
    "addToSyncKeys",
    "removeFromSyncKeys",
    "getSessionStorage",
    "setSessionStorage",
    "addToSessionStorage",
    "removeFromSessionStorage",
    "clearAllSessionsStorage"
  ];

  #sessionStorageTransferHandler(event) {
    if (!event.newValue) {
      return;
    }

    if (event.key === "getSessionStorage") {
      if (sessionStorage.length) {
        this.#localStorageSetItem("setSessionStorage", sessionStorage);
        localStorage.removeItem("setSessionStorage");
      }
    } else if (event.key === "setSessionStorage") {
      if (!this.#syncKeys.length) {
        this.#loadSyncKeys();
      }
      const data = JSON.parse(event.newValue);

      for (const key in data) {
        if (this.#syncKeysContains(key)) {
          this.#sessionStorageSetItem(key, JSON.parse(data[key]));
        }
      }

      this.#onInit();
    } else if (event.key === "addToSessionStorage") {
      const data = JSON.parse(event.newValue);

      this.#addToSessionStorageHelper(data.data, data.key);
    } else if (event.key === "removeFromSessionStorage") {
      this.#removeFromSessionStorageHelper(event.newValue);
    } else if (
      event.key === "clearAllSessionsStorage" &&
      sessionStorage.length
    ) {
      this.clearInstanceSessionStorage();
    } else if (event.key === "addToSyncKeys") {
      this.#addToSyncKeysHelper(event.newValue);
    } else if (event.key === "removeFromSyncKeys") {
      this.#removeFromSyncKeysHelper(event.newValue);
    }
  }

  initialiseStorageSyncListener() {
    if (LocalStoreManager.syncListenerInitialised === true) {
      return;
    }

    LocalStoreManager.syncListenerInitialised = true;
    window.addEventListener(
      "storage",
      this.#sessionStorageTransferHandler.bind(this),
      false
    );
  }

  disposeStorageSyncListener() {
    window.removeEventListener(
      "storage",
      this.#sessionStorageTransferHandler.bind(this),
      false
    );
    LocalStoreManager.syncListenerInitialised = false;
  }

  clearAllStorage() {
    this.clearAllSessionsStorage();
    this.clearLocalStorage();
  }

  clearAllSessionsStorage() {
    this.clearInstanceSessionStorage();
    localStorage.removeItem(LocalStoreManager.DBKEY_SYNC_KEYS);
    localStorage.setItem("clearAllSessionsStorage", "_dummy");
    localStorage.removeItem("clearAllSessionsStorage");
  }

  clearInstanceSessionStorage() {
    sessionStorage.clear();
    this.#syncKeys = [];
  }

  clearLocalStorage() {
    localStorage.clear();
  }

  saveSessionData(data, key = LocalStoreManager.DBKEY_USER_DATA, raw = false) {
    this.#testForInvalidKeys(key);

    this.#removeFromSyncKeys(key);
    localStorage.removeItem(key);
    this.#sessionStorageSetItem(key, data, raw);
  }

  saveSyncedSessionData(
    data,
    key = LocalStoreManager.DBKEY_USER_DATA,
    raw = false
  ) {
    this.#testForInvalidKeys(key);

    localStorage.removeItem(key);
    this.#addToSessionStorage(data, key, raw);
  }

  savePermanentData(
    data,
    key = LocalStoreManager.DBKEY_USER_DATA,
    raw = false
  ) {
    this.#testForInvalidKeys(key);

    this.#removeFromSessionStorage(key);
    this.#localStorageSetItem(key, data, raw);
  }

  moveDataToSessionStorage(key = LocalStoreManager.DBKEY_USER_DATA) {
    this.#testForInvalidKeys(key);

    const data = this.getData(key);

    if (data == null) {
      return;
    }

    this.saveSessionData(data, key);
  }

  // noinspection JSUnusedGlobalSymbols
  moveDataToSyncedSessionStorage(key = LocalStoreManager.DBKEY_USER_DATA) {
    this.#testForInvalidKeys(key);

    const data = this.getData(key);

    if (data == null) {
      return;
    }

    this.saveSyncedSessionData(data, key);
  }

  moveDataToPermanentStorage(key = LocalStoreManager.DBKEY_USER_DATA) {
    this.#testForInvalidKeys(key);

    const data = this.getData(key);

    if (data == null) {
      return;
    }

    this.savePermanentData(data, key);
  }

  exists(key = LocalStoreManager.DBKEY_USER_DATA) {
    let data = sessionStorage.getItem(key);

    if (data == null) {
      data = localStorage.getItem(key);
    }

    return data != null;
  }

  getData(key = LocalStoreManager.DBKEY_USER_DATA, raw = false) {
    this.#testForInvalidKeys(key);

    let data = this.#sessionStorageGetItem(key, raw);

    if (data == null) {
      data = this.#localStorageGetItem(key, raw);
    }

    return data;
  }

  getDataObject(key = LocalStoreManager.DBKEY_USER_DATA, isDateType = false) {
    let data = this.getData(key);

    if (data != null) {
      if (isDateType) {
        data = new Date(data);
      }
      return data;
    } else {
      return null;
    }
  }

  deleteData(key = LocalStoreManager.DBKEY_USER_DATA) {
    this.#testForInvalidKeys(key);

    this.#removeFromSessionStorage(key);
    localStorage.removeItem(key);
  }

  getInitEvent() {
    return this.#initEvent.asObservable();
  }

  #addToSessionStorage(data, key, raw = false) {
    this.#addToSessionStorageHelper(data, key, raw);
    this.#addToSyncKeysBackup(key);

    // trigger storage change event across tabs
    this.#localStorageSetItem("addToSessionStorage", { key, data });
    localStorage.removeItem("addToSessionStorage");
  }

  #addToSessionStorageHelper(data, key, raw = false) {
    this.#addToSyncKeysHelper(key);
    this.#sessionStorageSetItem(key, data, raw);
  }

  #removeFromSessionStorage(keyToRemove) {
    this.#removeFromSessionStorageHelper(keyToRemove);
    this.#removeFromSyncKeysBackup(keyToRemove);

    // trigger storage change event across tabs
    localStorage.setItem("removeFromSessionStorage", keyToRemove);
    localStorage.removeItem("removeFromSessionStorage");
  }

  #removeFromSessionStorageHelper(keyToRemove) {
    sessionStorage.removeItem(keyToRemove);
    this.#removeFromSyncKeysHelper(keyToRemove);
  }

  #testForInvalidKeys(key) {
    if (!key) {
      throw new Error("key cannot be empty");
    }

    if (this.#reservedKeys.some(x => x === key)) {
      throw new Error(
        `The storage key "${key}" is reserved and cannot be used. Please use a different key`
      );
    }
  }

  #syncKeysContains(key) {
    return this.#syncKeys.some(x => x === key);
  }

  #loadSyncKeys() {
    if (this.#syncKeys.length) {
      return;
    }

    this.#syncKeys = this.#getSyncKeysFromStorage();
  }

  #getSyncKeysFromStorage(defaultValue = []) {
    const data = this.#localStorageGetItem(LocalStoreManager.DBKEY_SYNC_KEYS);

    if (data == null) {
      return defaultValue;
    } else {
      return data;
    }
  }

  #addToSyncKeysBackup(key) {
    const storedSyncKeys = this.#getSyncKeysFromStorage();

    if (!storedSyncKeys.some(x => x === key)) {
      storedSyncKeys.push(key);
      this.#localStorageSetItem(
        LocalStoreManager.DBKEY_SYNC_KEYS,
        storedSyncKeys
      );
    }
  }

  #removeFromSyncKeysBackup(key) {
    const storedSyncKeys = this.#getSyncKeysFromStorage();

    const index = storedSyncKeys.indexOf(key);

    if (index > -1) {
      storedSyncKeys.splice(index, 1);
      this.#localStorageSetItem(
        LocalStoreManager.DBKEY_SYNC_KEYS,
        storedSyncKeys
      );
    }
  }

  #addToSyncKeysHelper(key) {
    if (!this.#syncKeysContains(key)) {
      this.#syncKeys.push(key);
    }
  }

  #removeFromSyncKeys(key) {
    this.#removeFromSyncKeysHelper(key);
    this.#removeFromSyncKeysBackup(key);

    localStorage.setItem("removeFromSyncKeys", key);
    localStorage.removeItem("removeFromSyncKeys");
  }

  #removeFromSyncKeysHelper(key) {
    const index = this.#syncKeys.indexOf(key);

    if (index > -1) {
      this.#syncKeys.splice(index, 1);
    }
  }

  // noinspection JSMethodCanBeStatic
  #localStorageSetItem(key, data, raw = false) {
    localStorage.setItem(key, raw ? data : JSON.stringify(data));
  }

  // noinspection JSMethodCanBeStatic
  #sessionStorageSetItem(key, data, raw = false) {
    sessionStorage.setItem(key, raw ? data : JSON.stringify(data));
  }

  // noinspection JSMethodCanBeStatic
  #localStorageGetItem(key, raw = false) {
    return raw
      ? localStorage.getItem(key)
      : jsonTryParse(localStorage.getItem(key));
  }

  // noinspection JSMethodCanBeStatic
  #sessionStorageGetItem(key, raw = false) {
    return raw
      ? sessionStorage.getItem(key)
      : jsonTryParse(sessionStorage.getItem(key));
  }

  #onInit() {
    setTimeout(() => {
      this.#initEvent.next();
      this.#initEvent.complete();
    });
  }
}

// noinspection JSUnusedGlobalSymbols
class SyncedSessionStorage {
  /**
   * {LocalStoreManager}
   */
  #storageManager;

  constructor(storageManager) {
    this.#storageManager = storageManager;
  }

  key(n) {
    return sessionStorage.key(n);
  }

  getItem(keyName) {
    return this.#storageManager.getData(keyName, true);
  }

  setItem(keyName, keyValue) {
    this.#storageManager.saveSyncedSessionData(keyValue, keyName, true);
  }

  removeItem(keyName) {
    this.#storageManager.deleteData(keyName);
  }

  clear() {
    this.#storageManager.clearAllSessionsStorage();
  }

  get length() {
    return 0;
  }
}

const localStoreManager = new LocalStoreManager();
const syncedSessionStorage = new SyncedSessionStorage(localStoreManager);

export { localStoreManager, syncedSessionStorage };
